import { useLayoutEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";

// Lista/menú de sugerencias anclada a un input. Se renderiza en un PORTAL con
// position:fixed para que NINGÚN contenedor con overflow la recorte, con z-index
// alto (por encima de filas, tarjetas y diálogos), altura máxima con scroll propio
// y "flip" hacia arriba si no hay espacio abajo. Funciona igual en móvil.
// Si el ancla está DENTRO de un Dialog (role="dialog"), el portal se monta ahí mismo
// (no en document.body): react-remove-scroll (usado por Radix Dialog) bloquea la
// rueda/touch de los elementos fuera del árbol del diálogo, así que portalar afuera
// dejaba la lista sin scroll. El Dialog tiene CSS transform (centrado), lo que lo
// convierte en containing block de sus descendientes "fixed": las coordenadas se
// recalculan relativas a su rect en vez de al viewport para que siga en el mismo lugar.
export const AnchoredList = ({ anchorRef, open, maxHeight = 240, minWidth = 0, className = "", children }) => {
  const [pos, setPos] = useState(null);

  const recompute = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const spaceBelow = vh - r.bottom;
    const spaceAbove = r.top;
    const flip = spaceBelow < Math.min(maxHeight, 180) && spaceAbove > spaceBelow;
    const mh = Math.min(maxHeight, Math.max(120, (flip ? spaceAbove : spaceBelow) - 8));
    const width = Math.max(r.width, minWidth);
    // Evitar que se salga por el borde derecho de la pantalla (importante en móvil).
    const leftViewport = Math.max(6, Math.min(r.left, vw - width - 6));
    const topViewport = flip ? undefined : Math.round(r.bottom + 2);
    const bottomViewport = flip ? Math.round(vh - r.top + 2) : undefined;
    const dialogEl = el.closest('[role="dialog"]');
    let originLeft = 0, originTop = 0, originBottom = 0;
    if (dialogEl) {
      const dr = dialogEl.getBoundingClientRect();
      originLeft = dr.left; originTop = dr.top; originBottom = vh - dr.bottom;
    }
    setPos({
      left: leftViewport - originLeft,
      width: Math.min(width, vw - 12),
      top: topViewport !== undefined ? topViewport - originTop : undefined,
      bottom: bottomViewport !== undefined ? bottomViewport - originBottom : undefined,
      maxHeight: mh,
      portalTarget: dialogEl || document.body,
    });
  }, [anchorRef, maxHeight, minWidth]);

  useLayoutEffect(() => {
    if (!open) return;
    recompute();
    const onMove = () => recompute();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, recompute]);

  if (!open || !pos) return null;
  return createPortal(
    <div data-anchored-list
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      style={{
        position: "fixed", left: pos.left, width: pos.width,
        top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight, zIndex: 9999,
        pointerEvents: "auto",
      }}
      className={`overflow-y-auto overscroll-contain bg-white border border-border rounded-sm shadow-lg ${className}`}>
      {children}
    </div>,
    pos.portalTarget
  );
};
